/**
 * ========================================
 * CENTRALIZED LEAD API SERVICE
 * ========================================
 * Single source of truth for ALL form submissions across the website
 * 
 * 📍 APIs Used:
 * 1. Geolocation API: https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation
 *    - Fetches user's location (country, city, state, IP)
 *    - Called automatically before form submission
 * 
 * 2. Lead Submission API: https://leadapi.crmbytes.com/api/Lead/AddLead
 *    - Submits lead data to CRM
 *    - Handles validation and redirects
 * 
 * 📝 Usage in Components:
 * 
 * import { submitLeadWithGeoLocation } from '../services/centralLeadApi';
 * 
 * const handleSubmit = async (e) => {
 *   e.preventDefault();
 *   setIsLoading(true);
 *   
 *   const formData = {
 *     name: 'John Doe',
 *     email: 'john@example.com',
 *     phone: '1234567890',           // ONLY numeric, no country code
 *     countryCode: '+1',              // Country dial code
 *     country: 'US',                  // 2-letter country code
 *     description: 'Need trademark help'
 *   };
 *   
 *   const packageInfo = {             // Optional
 *     title: 'Standard',
 *     amount: 149
 *   };
 *   
 *   const result = await submitLeadWithGeoLocation(
 *     formData, 
 *     packageInfo,                    // Can be null
 *     'Home Page Form'                // Context for tracking
 *   );
 *   
 *   if (result.success) {
 *     // Automatically redirects to /application/main?customerid=X
 *   } else {
 *     toast.error(result.message);
 *     setIsLoading(false);
 *   }
 * };
 * 
 * ⚠️ IMPORTANT PAYLOAD RULES:
 * - phoneNumber: MUST be numeric only (e.g., "1234567890")
 * - countryDialCode: Integer without + (e.g., 1, 31, 92)
 * - country: 2-letter code (US, PK, NL)
 * - geoLocation: Stringified JSON of geolocation response
 * 
 * ✅ Response Handling:
 * - 200: Success → Auto-redirect to /application/main?customerid={id}
 * - 400: Validation error → Show data.errorMessage
 * - 401: Unauthorized → Show data.message
 * 
 * 🔍 Currently used in:
 * - InquiryModal.jsx
 * - HeroSection.jsx
 * - LandingPage.jsx
 * - Contact.jsx
 * - LandingPageNew.jsx
 * - LandingPageOld.jsx
 */

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object|null>} Geolocation data or null if failed
 */
export const getGeoLocation = async () => {
  try {
    const response = await fetch('https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation', {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    if (!response.ok) {
      console.warn('Geolocation API returned non-OK status:', response.status);
      return null;
    }
    
    const data = await response.json();
    
    if (data.isSuccess && data.data) {
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return null;
  }
};

/**
 * Extract ONLY numeric phone number (no country code prefix, no special characters)
 * IMPORTANT: phoneNumber field must be numeric only as per API requirements
 * Example: "+31 777-888-999" becomes "777888999"
 */
const extractPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-numeric characters including +, spaces, dashes, parentheses
  return phone.replace(/\D/g, '');
};

/**
 * Extract country dial code as integer
 * Example: "+31" becomes 31, "+1" becomes 1
 */
const extractCountryDialCode = (countryCode) => {
  if (!countryCode) return 1;
  // Remove '+' and any non-numeric characters, then parse as integer
  const numeric = countryCode.replace(/\D/g, '');
  return parseInt(numeric) || 1;
};

/**
 * Submit lead to CRM API
 * @param {Object} formData - Form data from user
 * @param {Object} geoLocationData - Geolocation data from API
 * @param {Object} packageInfo - Optional package information {title, amount}
 * @param {string} buttonContext - Context of which button/form was used
 * @returns {Promise<Object>} Result object with success status and message
 */
export const submitLead = async (formData, geoLocationData = null, packageInfo = null, buttonContext = '') => {
  try {
    // Extract data from geolocation if available
    const city = geoLocationData?.data?.cityName || '';
    const state = geoLocationData?.data?.regionName || '';
    const ipAddress = geoLocationData?.data?.ipAddress || '';
    
    // Get country from geolocation or form data
    const country = geoLocationData?.data?.countryCode || formData.country || 'US';
    
    // Build description with context
    let description = formData.description || 'IP protection inquiry';
    if (buttonContext) {
      description = `${buttonContext}\n\n${description}`;
    }
    
    // Prepare payload according to API spec
    const payload = {
      name: formData.name || formData.fullName || '',
      email: formData.email || '',
      countryDialCode: extractCountryDialCode(formData.countryCode),
      country: country,
      phoneNumber: extractPhoneNumber(formData.phone),
      ipAddress: ipAddress,
      description: description,
      pageUrl: window.location.origin + window.location.pathname,
      city: city,
      state: state,
      geoLocation: geoLocationData ? JSON.stringify(geoLocationData) : '',
      packageTitle: packageInfo?.title || '',
      packageAmount: packageInfo?.amount || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch('https://leadapi.crmbytes.com/api/Lead/AddLead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    console.log('Lead API Response:', data);

    // Handle different status codes as per spec
    if (response.status === 200 && data.isSuccess) {
      // Success - redirect to application page
      if (data.data?.customerId) {
        const redirectUrl = `/application/main?customerid=${data.data.customerId}`;
        console.log('Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
      }
      return {
        success: true,
        message: data.data?.message || 'Lead created successfully',
        data: data.data
      };
    } else if (response.status === 401) {
      // Unauthorized - show message from API
      return {
        success: false,
        message: data.message || 'Unauthorized access'
      };
    } else if (response.status === 400) {
      // Bad Request - show errorMessage from API
      return {
        success: false,
        message: data.errorMessage || data.data?.message || 'Invalid request data'
      };
    } else {
      // Other errors
      return {
        success: false,
        message: data.errorMessage || data.message || 'Something went wrong. Please try again.'
      };
    }
  } catch (error) {
    console.error('Lead Submission Error:', error);
    return {
      success: false,
      message: 'Failed to submit. Please check your connection and try again.'
    };
  }
};

/**
 * Complete flow: Get geolocation, then submit lead
 * This is the main function to call from forms
 * @param {Object} formData - Form data from user
 * @param {Object} packageInfo - Optional package information {title, amount}
 * @param {string} buttonContext - Context of which button/form was used
 * @returns {Promise<Object>} Result object with success status and message
 */
export const submitLeadWithGeoLocation = async (formData, packageInfo = null, buttonContext = '') => {
  try {
    // First, get geolocation
    const geoLocationData = await getGeoLocation();
    
    if (geoLocationData) {
      console.log('Geolocation fetched successfully:', geoLocationData.data?.countryName);
    } else {
      console.warn('Could not fetch geolocation, proceeding without it');
    }
    
    // Then submit lead with geolocation data
    const result = await submitLead(formData, geoLocationData, packageInfo, buttonContext);
    
    return result;
  } catch (error) {
    console.error('Submit Lead with Geolocation Error:', error);
    return {
      success: false,
      message: 'An error occurred. Please try again.'
    };
  }
};

/**
 * Get country information from geolocation for display purposes
 * @returns {Promise<Object>} Country info {countryCode, countryName, flag}
 */
export const getCountryInfo = async () => {
  try {
    const geoData = await getGeoLocation();
    
    if (geoData?.data) {
      return {
        countryCode: geoData.data.countryCode,
        countryName: geoData.data.countryName,
        region: geoData.data.regionName,
        city: geoData.data.cityName
      };
    }
    
    return null;
  } catch (error) {
    console.error('Get Country Info Error:', error);
    return null;
  }
};
